import { PanelLeft } from 'lucide-react';
import { useLocale } from '@/components/common/locale-provider';
import { useRegulationsSidebar } from '@/components/regulations/regulations-context';
import { Button } from '@/components/ui/button';
import { useOptionalSidebar } from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useArtifactSplitLayout } from '@/hooks/use-artifact-split-layout';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTablet } from '@/hooks/use-tablet';
import { useTranslations } from '@/hooks/use-translations';

export function SidebarToggle() {
  const sidebar = useOptionalSidebar();

  if (!sidebar) {
    return null;
  }

  return <ConnectedSidebarToggle sidebar={sidebar} />;
}

function ConnectedSidebarToggle({
  sidebar: { toggleSidebar, setOpen, state },
}: {
  sidebar: NonNullable<ReturnType<typeof useOptionalSidebar>>;
}) {
  const { isOpen: isRegulationsOpen, close: closeRegulations } =
    useRegulationsSidebar();
  const isMobile = useIsMobile();
  const isTablet = useTablet();
  const { locale } = useLocale();
  const { t } = useTranslations(locale);
  const artifactSplitLayout = useArtifactSplitLayout();

  const handleToggle = () => {
    // If regulations sidebar is open and main sidebar is collapsed,
    // close regulations and expand main sidebar in tablet mode
    if (isRegulationsOpen && state === 'collapsed' && !isMobile && isTablet) {
      closeRegulations();
      setOpen(true);
      return;
    }
    // Otherwise use default toggle behavior
    toggleSidebar();
  };

  if (artifactSplitLayout.isActive) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          data-testid="sidebar-toggle-button"
          onClick={handleToggle}
          variant="outline"
          className="px-2 h-9"
        >
          <PanelLeft size={16} />
        </Button>
      </TooltipTrigger>
      <TooltipContent align="start">
        {t('sidebar.toggleSidebar')}
      </TooltipContent>
    </Tooltip>
  );
}
